import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  WidgetCustomizationAction,
  WidgetCustomizationActionCreateAttributes,
} from "~/api/Api";
import { expandSourceFilter } from "~/helpers/sourceFilter";
import { isSiteLevelWidgetCustomizationAction } from "~/helpers/widgetCustomizationAction";

import { apiClient } from "../../../api/ApiClient";
import { queryKeys } from "../../../api/queryKeys";
import { Icon } from "../../../assets/icons/Icon";
import NoData from "../../containers/NoData";
import Button from "../../mantine/button/Button";
import { PaginationConfig } from "../../ui/pagination/Pagination";
import { SearchBar } from "../../ui/search/SearchBar";
import Spinner from "../../ui/spinner/Spinner";
import { ContentItem } from "../ContentItems";
import ContentItems from "../ContentItems";
import { ApproveAllConfirmationModal } from "../modals/ApproveAllConfirmationModal/ApproveAllConfirmationModal";
import PageListItem from "./PagesListItem";
import styles from "./styles.module.scss";

export interface Page {
  id: number;
  path_and_query: string;
  title?: string;
  approved: number;
  total: number;
}
export interface PageListItemProps {
  page: {
    id: number;
    url: string;
    title?: string;
    approved: number;
    total: number;
  };
  isPending?: boolean;
}
export interface PagesStats {
  active: number;
  total: number;
}
export interface ApproveData {
  kind: string;
  variant: string;
  site_id?: number;
  page_id?: number;
}
export interface MutationResponse {
  ids?: number[];
  success?: boolean;
  kind?: string;
  variant?: string;
  page_id?: number;
  site_id?: number;
  message?: string;
  status?: string;
  total_affected?: number;
  errors?: string[];
  timestamp?: string;
}

export interface PendingStatusesAndPermissionsResponse {
  pendingItems: WidgetCustomizationAction[];
  approvePermission: boolean;
  status?: number;
}

export interface PendingState {
  isSiteWidePending: boolean;
  isCurrentPagePending: boolean;
  isItemPending: (itemId: number) => boolean;
}

export interface PagesListProps {
  data: Page[];
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isPagesLoading: boolean;
  isInitialLoading: boolean;
  currentItem: ContentItem[];
  setSearchTerm: (searchTerm: string) => void;
  searchTerm: string;
  title?: string;
  description?: string;
  currentPage?: ContentItem | undefined | null;
  setCurrentPage?: (page: Page) => void;
  group?: "enhancement" | "ai" | "schema" | "ai_instructions";
  onMutationSuccess?: (showLoadingState?: boolean) => void;
  pagesStats?: PagesStats;
  pendingStatuses?: WidgetCustomizationAction[];
  approvePermission?: boolean;
  isPendingStatusesAndPermissionsFetching?: boolean;
  isFetchingStats?: boolean;
  isLoading?: boolean;
  setCurrentItem: (item: ContentItem[]) => void;
  itemAuthorizations?: string[][];
  sourceFilter?: "auto" | "manual";
  outdatedFilter?: boolean;
}

const PagesList = ({
  isLoading,
  data,
  pagination,
  setPagination,
  isPagesLoading,
  isInitialLoading,
  currentItem,
  title = "Live Editor",
  description,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  group = "ai",
  onMutationSuccess,
  pagesStats = { active: 0, total: 0 },
  pendingStatuses,
  approvePermission,
  isPendingStatusesAndPermissionsFetching,
  isFetchingStats,
  setCurrentItem,
  itemAuthorizations = [],
  sourceFilter,
  outdatedFilter,
}: PagesListProps) => {
  const queryClient = useQueryClient();
  // ai.md manual actions need the AI-engine + prerendering add-ons, surfaced as
  // the per-row member `update` ability (uniform across the site). Other groups
  // are unaffected. Gates the site-wide "Approve All".
  const canUpdate =
    group === "ai_instructions"
      ? itemAuthorizations.some((auth) => auth?.includes("update"))
      : true;
  const [isPageSuggestionsApproved, setIsPageSuggestionsApproved] =
    useState<boolean>(false);
  const [isPageApproveClicked, setIsPageApproveClicked] =
    useState<boolean>(false);
  const [shouldHandleAfterApprove, setShouldHandleAfterApprove] =
    useState<boolean>(false);
  const allPagesLoaded = pagination.currentPage === pagination.totalPages;
  const { id } = useParams();
  const bottomRef = useRef<HTMLDivElement>(null);
  const [
    isApproveAllConfirmationModalOpen,
    setIsApproveAllConfirmationModalOpen,
  ] = useState<boolean>(false);

  const {
    mutate: approve,
    mutateAsync: approveAsync,
    isPending: isPendingApprove,
  } = useMutation<
    WidgetCustomizationAction,
    Error,
    WidgetCustomizationActionCreateAttributes,
    unknown
  >({
    mutationFn: async (data) => {
      // Site-level actions must NOT carry the UI filters `source` or
      // `outdated` — those filters are scoped to the currently viewed page
      // context and would incorrectly narrow a site-wide action. Scoped
      // actions (page_id or explicit ids[]) propagate the active filters.
      // The cast widens the readonly array from expandSourceFilter to the
      // generated single-value `source` type — BE accepts an array.
      const apiSources = expandSourceFilter(sourceFilter) as unknown as
        | WidgetCustomizationActionCreateAttributes["source"]
        | undefined;
      const payload: WidgetCustomizationActionCreateAttributes =
        isSiteLevelWidgetCustomizationAction(data)
          ? data
          : {
              ...data,
              ...(apiSources ? { source: apiSources } : {}),
              ...(outdatedFilter ? { outdated: true } : {}),
            };

      const response = await apiClient.sites.widgetCustomizationActionsCreate(
        Number(id),
        payload,
      );
      const dataResponse = response?.data?.["widget/customization_action"];

      return dataResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.pendingStatusesAndPermissions(group),
      });
      const pendingStatusesData = queryClient.getQueryData<{
        pendingItems: WidgetCustomizationAction[];
      }>(["pendingStatusesAndPermissions", group]);

      if ((pendingStatusesData?.pendingItems ?? []).length > 0) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.pendingStatusesAndPermissions(group),
        });
      }

      setShouldHandleAfterApprove(true);
    },
    onError: (error) => {
      console.error("Error approving:", error);
    },
  });

  // After widgetCustomizationActionsCreate() wait for the right moment
  // to handle categories and objects 'approve' state
  useEffect(() => {
    // wait for all pendingItems to change status
    if (
      shouldHandleAfterApprove &&
      !isPendingStatusesAndPermissionsFetching &&
      pendingStatuses &&
      pendingStatuses.length === 0
    ) {
      // Use prefix arrays to match regardless of sourceFilter value
      queryClient.invalidateQueries({
        queryKey: ["widgetCustomizationsStats", group],
      });

      if (onMutationSuccess) {
        onMutationSuccess(false);
      }
      if (currentPage?.id) {
        queryClient.invalidateQueries({
          queryKey: ["contentAutomations", group, currentPage.id],
        });
      }
      setIsPageSuggestionsApproved(false);
      setIsPageApproveClicked(false);
      setShouldHandleAfterApprove(false);
    }
  }, [
    id,
    shouldHandleAfterApprove,
    isPendingStatusesAndPermissionsFetching,
    pendingStatuses,
    currentPage?.id,
    group,
    onMutationSuccess,
    queryClient,
  ]);

  const allPagesApproved = pagesStats?.active === pagesStats?.total;

  const handleLoadMore = () => {
    if (pagination.currentPage < (pagination?.totalPages ?? 0)) {
      setPagination({
        ...pagination,
        currentPage: pagination.currentPage + 1,
      });
    }
  };

  const handlePageSelect = (page: Page) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
  };

  const hasSiteWidePendingOperation: boolean =
    pendingStatuses?.some(
      (item: WidgetCustomizationAction) =>
        item.status === "pending" &&
        item.page_id === null &&
        Array.isArray(item.ids) &&
        item.ids.length === 0 &&
        item.site_id === Number(id),
    ) ?? false;

  const isPagesLoadingNoData = isPagesLoading && data?.length === 0;

  return (
    <div className={styles.main}>
      <div className={styles.mainContent}>
        {isInitialLoading ? (
          <Spinner />
        ) : (
          <>
            <div className={styles.mainContentSidebar}>
              <div className={styles.pageListWrapper}>
                <div className={styles.pageList}>
                  <div className={styles.pageListHeader}>
                    <div>
                      <h2 className={styles.pageListTitle}>{title}</h2>
                      <div className={styles.pageListStats}>
                        {pagesStats.total > 0 &&
                          (pagesStats.active === pagesStats.total ? (
                            <span className={styles.pageListStatsAllApproved}>
                              {`All ${pagesStats.total} Approved`}
                            </span>
                          ) : (
                            <>
                              <span
                                className={
                                  pagesStats.active > 0
                                    ? styles.pageListStatsApprovedCount
                                    : ""
                                }
                              >
                                {pagesStats.active}
                              </span>
                              {` of ${pagesStats.total} approved`}
                            </>
                          ))}
                      </div>
                    </div>
                    {pagination.totalItems > 0 && (
                      <Button
                        variant="outlinedIconSmall"
                        onClick={() =>
                          setIsApproveAllConfirmationModalOpen(true)
                        }
                        disabled={
                          !canUpdate ||
                          isPendingApprove ||
                          !approvePermission ||
                          hasSiteWidePendingOperation ||
                          (pendingStatuses?.length ?? 0) > 0 ||
                          isFetchingStats
                        }
                      >
                        {hasSiteWidePendingOperation ||
                        isFetchingStats ||
                        isPendingApprove ? (
                          <Spinner size="1rem" />
                        ) : (
                          <Icon
                            iconName={
                              allPagesApproved
                                ? "GreenCheckmarkIcon"
                                : "CloseIconCircled"
                            }
                            width="1rem"
                            height="1rem"
                          />
                        )}
                        {allPagesApproved
                          ? "All Approved"
                          : "I’m Feeling Lucky"}
                      </Button>
                    )}
                    <ApproveAllConfirmationModal
                      isOpen={isApproveAllConfirmationModalOpen}
                      onClose={() =>
                        setIsApproveAllConfirmationModalOpen(false)
                      }
                      toggleApprove={async () => {
                        await approveAsync({
                          kind: allPagesApproved ? "unapprove" : "approve",
                          variant: group,
                        });
                      }}
                      isApproveLoading={isPendingApprove}
                      isAllApproved={allPagesApproved}
                      autoApprovalField={(() => {
                        switch (group) {
                          case "schema":
                            return "schemas_auto_approval";
                          case "ai":
                            return "customizations_auto_approval";
                          case "ai_instructions":
                            return "ai_instructions_auto_approval";
                          default:
                            return undefined;
                        }
                      })()}
                    />
                  </div>
                  {description && (
                    <p className={styles.pageListDescription}>{description}</p>
                  )}
                  <div className={styles.pageListSearch}>
                    <SearchBar
                      onChange={setSearchTerm}
                      value={searchTerm}
                      placeholder="Search"
                    />
                  </div>
                  {isPagesLoadingNoData && (
                    <div className={styles.pageSpinnertWrapper}>
                      <Spinner />
                    </div>
                  )}
                  {data?.length === 0 && !isPagesLoading && (
                    <NoData title="No Pages Found" />
                  )}
                  {data?.map((page) => {
                    const pageItem = {
                      id: page.id,
                      url: page.path_and_query,
                      title: page?.title,
                      approved: page.approved,
                      total: page.total,
                    };

                    const isPagePending = pendingStatuses?.some(
                      (item) =>
                        item.status === "pending" && item.page_id === page.id,
                    );

                    return (
                      <div
                        // @ts-expect-error TS2551 non-existing className
                        className={`${styles.pageListItem} ${currentPage?.id === page.id ? styles.pageItemActive : ""} ${isPagePending ? styles.pageItemPending : ""}`}
                        onClick={() => handlePageSelect(page)}
                        key={page.id}
                      >
                        <PageListItem
                          page={pageItem}
                          isPending={isPagePending}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className={styles.mainContentFooter}>
                <Button
                  disabled={allPagesLoaded || isPagesLoading}
                  variant="outlinedIconSmall"
                  onClick={handleLoadMore}
                >
                  {isPagesLoading && <Spinner size="1rem" />}
                  {allPagesLoaded ? "All Pages Loaded" : "Load More"}
                </Button>
              </div>
              {/*
               // @ts-expect-error TS2339 non-existing className */}
              <div ref={bottomRef} className={styles.bottomAnchor} />
            </div>

            <div className={styles.mainContentMain}>
              {!currentPage && !isPagesLoading ? (
                <NoData title="No Page Selected" />
              ) : (
                <ContentItems
                  setCurrentItem={setCurrentItem}
                  isPagesLoadingNoData={isPagesLoadingNoData}
                  isFetchingStats={isFetchingStats}
                  approvePermission={approvePermission}
                  isPageSuggestionsApproved={isPageSuggestionsApproved}
                  setIsPageSuggestionsApproved={setIsPageSuggestionsApproved}
                  isPageApproveClicked={isPageApproveClicked}
                  setIsPageApproveClicked={setIsPageApproveClicked}
                  approve={(data) => approve(data)}
                  isPending={isLoading && !isPendingApprove}
                  isLoading={isLoading}
                  data={currentItem}
                  currenPageUrl={currentPage?.path_and_query}
                  approved={currentPage?.approved}
                  total={currentPage?.total}
                  pageId={currentPage?.id}
                  group={group}
                  pendingStatuses={pendingStatuses}
                  isPendingAprove={
                    isPendingApprove || isPendingStatusesAndPermissionsFetching
                  }
                  itemAuthorizations={itemAuthorizations}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PagesList;
